// Terminal logging helpers — chalk for color, ora for spinners.
// All Avatar CLI output should funnel through here for consistent UX.
import chalk from "chalk";
import ora, { type Ora } from "ora";

type LogFn = (message: string) => void;

export const log: {
  info: LogFn;
  success: LogFn;
  warn: LogFn;
  error: LogFn;
  dim: LogFn;
  plain: LogFn;
} = {
  info: (m) => process.stdout.write(`${chalk.blue("ℹ")} ${m}\n`),
  success: (m) => process.stdout.write(`${chalk.green("✓")} ${m}\n`),
  warn: (m) => process.stdout.write(`${chalk.yellow("⚠")} ${m}\n`),
  error: (m) => process.stderr.write(`${chalk.red("✗")} ${m}\n`),
  dim: (m) => process.stdout.write(`${chalk.dim(m)}\n`),
  plain: (m) => process.stdout.write(`${m}\n`),
};

// Spinner wrapper — handles non-TTY by degrading to plain output.
export function spinner(text: string): Ora {
  return ora({
    text,
    spinner: "dots",
    isEnabled: process.stdout.isTTY ?? false,
  }).start();
}

// Long-running spinner với elapsed time — update text mỗi giây.
// Trả về { stop } để caller clear interval + stop spinner đúng cách.
// Format: "⠋ <prefix> (1:23)" — user biết tool alive + chạy bao lâu.
export function spinnerWithElapsed(prefix: string): {
  succeed: (text: string) => void;
  fail: (text: string) => void;
  stop: () => void;
} {
  const startMs = Date.now();
  const sp = spinner(`${prefix} (0:00)`);
  const formatElapsed = (): string => {
    const sec = Math.floor((Date.now() - startMs) / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };
  const interval = setInterval(() => {
    sp.text = `${prefix} (${formatElapsed()})`;
  }, 1000);
  return {
    succeed: (text: string) => {
      clearInterval(interval);
      sp.succeed(`${text} (${formatElapsed()})`);
    },
    fail: (text: string) => {
      clearInterval(interval);
      sp.fail(`${text} (${formatElapsed()})`);
    },
    stop: () => {
      clearInterval(interval);
      sp.stop();
    },
  };
}

// Chalk re-export so commands don't all import chalk separately.
export { chalk };
